<template>
    <span v-if="visibleCreateActions.length > 0">
        <prime-button
            label="Create Frequency Set"
            :icon="hasMultipleActions ? 'pi pi-chevron-down' : null"
            iconPos="right"
            class="create-frequency-set-button"
            data-test-id="create-frequency-set-button"
            @click="handleClick"
        />
        <prime-menu
            v-if="hasMultipleActions"
            ref="createMenu"
            data-test-id="create-frequency-set-menu"
            :model="visibleCreateActions"
            :pt="{
                item: ({ context }) => ({
                    'data-test-id': context.item.testId,
                }),
            }"
            popup
        >
            <template #item="{ item, props }">
                <a
                    class="p-menu-item-link create-frequency-set-item"
                    v-bind="props.action"
                >
                    <span :class="item.icon" />
                    <span>Create</span>
                    <frequency-set-type-badge
                        :type="item.frequencySetType"
                        :data-test-id="`${item.testId}-type`"
                    />
                </a>
            </template>
        </prime-menu>
    </span>
</template>

<script>
import PrimeButton from 'primevue/button'
import PrimeMenu from 'primevue/menu'
import Permissions from '@/mixins/permissions'
import frequencySetTypes from '@/constants/frequency-set-types.js'
import FrequencySetTypeBadge from '@/components/FrequencySetTypeBadge.vue'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import featureFlagsEnum from '@/constants/feature-flags'

export default {
    components: {
        PrimeButton,
        PrimeMenu,
        FrequencySetTypeBadge,
    },

    mixins: [Permissions, needsOrganizationSlug],

    data() {
        return {
            createActions: [
                {
                    label: `Create ${frequencySetTypes.X_SERIES.label} Frequency Set`,
                    frequencySetType: frequencySetTypes.X_SERIES.type,
                    icon: 'pi pi-plus',
                    permission: 'create frequency sets',
                    testId: 'create-x-frequency-set',
                    command: () =>
                        this.$router.push(
                            `/${this.organizationSlug}/frequency-sets/create?type=${frequencySetTypes.X_SERIES.type}`
                        ),
                },
                {
                    label: `Create ${frequencySetTypes.NEXT_GEN.label} Frequency Set`,
                    frequencySetType: frequencySetTypes.NEXT_GEN.type,
                    icon: 'pi pi-plus',
                    permission: 'create frequency sets',
                    featureFlags: [featureFlagsEnum.NEXTGEN_FREQUENCY_SETS_ENABLED],
                    testId: 'create-nextgen-frequency-set',
                    command: () =>
                        this.$router.push(
                            `/${this.organizationSlug}/frequency-sets/create?type=${frequencySetTypes.NEXT_GEN.type}`
                        ),
                },
            ],
        }
    },

    computed: {
        visibleCreateActions() {
            return this.createActions.filter((action) => {
                const { permission, organizationFeatures = [], featureFlags = [] } = action

                return (
                    this.can(permission) &&
                    this.hasAllOrgFeatures(organizationFeatures) &&
                    this.hasAllFeatureFlags(featureFlags)
                )
            })
        },
        hasMultipleActions() {
            return this.visibleCreateActions.length > 1
        },
    },

    methods: {
        handleClick(event) {
            // With a single action, act as a plain button and run it directly;
            // only open the dropdown menu when there's more than one choice
            if (this.hasMultipleActions) {
                this.$refs.createMenu.toggle(event)
                return
            }

            this.visibleCreateActions[0].command()
        },
        hasAllOrgFeatures(features) {
            return features.every((feature) => this.$store.getters.organizationHasFeature(feature))
        },
        hasAllFeatureFlags(flags) {
            return flags.every((flag) => this.$store.getters.hasFeatureFlag(flag))
        },
    },
}
</script>
<style lang="scss" scoped>
.create-frequency-set-item {
    display: flex;
    align-items: center;
    gap: 6px;
}

.create-frequency-set-button {
    background-color: #59c966 !important;
    border-color: #59c966 !important;
    color: #28522d;
    border-radius: 50px;
    font-size: 0.875rem;
    line-height: 1;
    padding-top: 5px;
    padding-bottom: 5px;
}
</style>
